import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import PageContainer from "../components/PageContainer";
import Cropper from "react-easy-crop";
import getCroppedImg from "../utils/cropImage";
import LazyImage from "../components/LazyImage"; // Add this import
import imageCompression from "browser-image-compression";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import LogoutIcon from "@mui/icons-material/Logout";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Utility to apply sharpening using canvas
async function sharpenImage(file, strength = 1) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      // Sharpen kernel
      const weights = [
        0, -strength, 0,
        -strength, 1 + 4 * strength, -strength,
        0, -strength, 0
      ];
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      const side = 3;
      const halfSide = Math.floor(side / 2);
      const sw = canvas.width;
      const sh = canvas.height;
      const output = ctx.createImageData(sw, sh);
      const dst = output.data;

      for (let y = 0; y < sh; y++) {
        for (let x = 0; x < sw; x++) {
          for (let c = 0; c < 4; c++) {
            let i = (y * sw + x) * 4 + c;
            let newValue = 0;
            for (let ky = 0; ky < side; ky++) {
              for (let kx = 0; kx < side; kx++) {
                const px = x + kx - halfSide;
                const py = y + ky - halfSide;
                if (px >= 0 && px < sw && py >= 0 && py < sh) {
                  const ki = (ky * side + kx);
                  const pi = (py * sw + px) * 4 + c;
                  newValue += pixels[pi] * weights[ki];
                }
              }
            }
            dst[i] = Math.min(255, Math.max(0, newValue));
          }
        }
      }
      ctx.putImageData(output, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) resolve(new File([blob], file.name, { type: "image/webp" }));
        else reject(new Error("Sharpening failed"));
      }, "image/webp");
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const BUCKET_NAME = "manga_versus";

// Update normalizeString to handle Unicode edge cases:
function normalizeString(str) {
  return (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export default function CharacterManager() {
  const [session, setSession] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [form, setForm] = useState({
    name: "",
    manga: "",
    affiliation: [],
    role: "",
    gender: "",
    rarity: "",
    imageFile: null,
    audioFile: null,
  });

  const [modifyCharacter, setModifyCharacter] = useState(null);
  const [modifyForm, setModifyForm] = useState({
    id: null,
    name: "",
    manga: "",
    affiliation: [],
    role: "",
    gender: "",
    imageFile: null,
    audioFile: null,
    image_url: null,
    victory_audio: null,
    rarity: "",
  });

  // Image cropping states
  const [imageCropDialogOpen, setImageCropDialogOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isModifyingImage, setIsModifyingImage] = useState(false);
  const imageInputRef = useRef();

  const [mangaOptions, setMangaOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [affiliationOptions, setAffiliationOptions] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [rarityOptions, setRarityOptions] = useState([]); // Add state for rarity options

  const [playingAudio, setPlayingAudio] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState({
    manga: "",
    gender: "",
    role: "",
    affiliation: "",
    rarity: "", // Add rarity to the filter state
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [pasteError, setPasteError] = useState(""); // <-- New state for paste error

  // Auth session
  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      if (data.session) {
        const { data: userData } = await supabase
          .from("users")
          .select("role")
          .eq("id", data.session.user.id)
          .single();
        setUserRole(userData?.role);
      }
    };
    getSession();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) setUserRole(null);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error("Logout error:", error);
  };

  // Fetch dropdowns (including rarity options)
  useEffect(() => {
    const fetchDropdowns = async () => {
      const { data } = await supabase.from("dropdowns").select("*");
      const mangaRow = data.find((r) => r.category === "manga");
      const roleRow = data.find((r) => r.category === "role");
      const genderRow = data.find((r) => r.category === "gender");
      const rarityRow = data.find((r) => r.category === "rarity"); // Fetch rarity options

      setMangaOptions(mangaRow?.options || []);
      setRoleOptions(roleRow?.options || []);
      setGenderOptions(genderRow?.options || []);
      setRarityOptions(rarityRow?.options || []); // Set rarity options
    };
    fetchDropdowns();
    fetchCharacters();
  }, []);

  // Fetch affiliations when manga filter changes
  useEffect(() => {
    const fetchAffiliations = async () => {
      if (!filter.manga) return setAffiliationOptions([]);
      const { data } = await supabase
        .from("dropdowns")
        .select("options")
        .eq("category", "affiliation")
        .eq("manga", filter.manga)
        .single();
      setAffiliationOptions(data?.options || []);
    };
    fetchAffiliations();
  }, [filter.manga]);

  // Add this new useEffect for form affiliations
  useEffect(() => {
    const fetchFormAffiliations = async () => {
      if (!form.manga) return;
      const { data } = await supabase
        .from("dropdowns")
        .select("options")
        .eq("category", "affiliation")
        .eq("manga", form.manga)
        .single();
      // Only update if filter.manga is not set (to avoid overriding filter affiliations)
      if (!filter.manga) {
        setAffiliationOptions(data?.options || []);
      }
    };
    fetchFormAffiliations();
  }, [form.manga, filter.manga]);

  // Image upload and crop handlers
  const handleImageUpload = (e, isModify = false) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIsModifyingImage(isModify);
      setImageCropDialogOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleImageSave = async () => {
    if (!imageFile || !croppedAreaPixels) return;
    try {
      // Crop and convert the image to webp
      const { blob } = await getCroppedImg(imagePreview, croppedAreaPixels);
      
      if (isModifyingImage) {
        setModifyForm(prev => ({ ...prev, imageFile: blob }));
      } else {
        setForm(prev => ({ ...prev, imageFile: blob }));
      }
      
      setImageCropDialogOpen(false);
      setImageFile(null);
      setImagePreview(null);
      setCroppedAreaPixels(null);
    } catch (err) {
      console.error("Failed to process image:", err);
      alert("Failed to process image.");
    }
  };

  const handleImageButtonClick = (isModify = false) => {
    if (imageInputRef.current) {
      imageInputRef.current.value = null; // reset so same file can be picked again
      imageInputRef.current.dataset.isModify = isModify;
      imageInputRef.current.click();
    }
  };

  const handlePasteImage = async (isModify = false) => {
    try {
      setPasteError("");
      const clipboardItems = await navigator.clipboard.read();
      
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            const blob = await clipboardItem.getType(type);
            const file = new File([blob], 'pasted-image.png', { type: blob.type });
            
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            setIsModifyingImage(isModify);
            setImageCropDialogOpen(true);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
            return;
          }
        }
      }
      setPasteError("No image found in clipboard");
    } catch (err) {
      console.error("Failed to paste image:", err);
      setPasteError("Failed to access clipboard. Make sure you have copied an image first.");
    }
  };

  // Remove filteredCharacters, and use search/filter/pagination in fetchCharacters
  const fetchCharacters = async () => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("characters")
      .select("*", { count: "exact" })
      .order("name", { ascending: true });

    // Apply filters
    if (filter.manga) {
      query = query.eq("manga", filter.manga);
    }
    if (filter.gender) {
      query = query.eq("gender", filter.gender);
    }
    if (filter.role) {
      query = query.eq("role", filter.role);
    }
    if (filter.affiliation) {
      query = query.contains("affiliation", [filter.affiliation]);
    }
    if (filter.rarity) {
      query = query.eq("rarity", filter.rarity); // Ensure rarity filter is applied
    }

    const { data, error, count } = await query.range(from, to); // Add range for pagination
    if (error) {
      console.error("Error fetching characters:", error);
      setCharacters([]);
      setTotalCount(0);
      return;
    }

    // Apply normalized search on the client side
    let filtered = data;
    if (searchTerm.trim()) {
      filtered = filtered.filter((char) =>
        normalizeString(char.name).includes(normalizeString(searchTerm))
      );
    }

    setTotalCount(count || filtered.length); // Use count from Supabase if available
    setCharacters(filtered);
  };

  // Ensure `useEffect` watches all relevant dependencies
  useEffect(() => {
    fetchCharacters();
    // eslint-disable-next-line
  }, [page, pageSize, searchTerm, filter]);

  const handleChange = (field, value, isModify = false) => {
    if (isModify) {
      setModifyForm((prev) => ({ ...prev, [field]: value }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleAffiliationSelect = (value, isModify = false) => {
    const targetForm = isModify ? modifyForm : form;
    if (!targetForm.affiliation.includes(value)) {
      handleChange("affiliation", [...targetForm.affiliation, value], isModify);
    }
  };

  const removeAffiliation = (value, isModify = false) => {
    const targetForm = isModify ? modifyForm : form;
    handleChange(
      "affiliation",
      targetForm.affiliation.filter((a) => a !== value),
      isModify
    );
  };

  // Update resizeToPortrait to use lossless WebP compression for optimal efficiency
  function resizeToPortrait(file, width = 270, height = 480) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // Use lossless compression for WebP
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(new File([blob], file.name, { type: "image/webp" }));
            else reject(new Error("Resize failed"));
          },
          "image/webp",
          undefined // undefined quality triggers lossless WebP if supported
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.imageFile) return alert("Please select and crop an image file!");

    let uploadFile = form.imageFile;
    uploadFile = await resizeToPortrait(uploadFile, 270, 480);

    try {
      const safeName = form.name.replace(/\s+/g, "").replace(/[^\w]/g, "");

      const { data: imgData, error: imgError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(`images/characters_pictures/${safeName}Picture.webp`, uploadFile, {
          upsert: true,
          contentType: "image/webp",
          cacheControl: "31536000",
        });
      if (imgError) throw imgError;
      const { data: imgPublic } = supabase.storage.from(BUCKET_NAME).getPublicUrl(imgData.path);
      const image_url = imgPublic?.publicUrl;

      let victory_audio = null;
      if (form.audioFile) {
        const { data: audioData, error: audioError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(`audio/characters_victory/${safeName}Victory`, form.audioFile, { upsert: true });
        if (audioError) throw audioError;
        const { data: audioPublic } = supabase.storage.from(BUCKET_NAME).getPublicUrl(audioData.path);
        victory_audio = audioPublic?.publicUrl;
      }

      const { error: insertError } = await supabase.from("characters").insert([
        {
          name: form.name,
          manga: form.manga,
          affiliation: form.affiliation,
          role: form.role,
          gender: form.gender,
          rarity: form.rarity, // Include rarity in the insert
          image_url,
          victory_audio,
        },
      ]);
      if (insertError) throw insertError;

      setForm({ name: "", manga: "", affiliation: [], role: "", gender: "", rarity: "", imageFile: null, audioFile: null });
      fetchCharacters();
      alert("Character added successfully!");
    } catch (err) {
      console.error("Error adding character:", err);
      alert("Error adding character. Check console for details.");
    }
  };

  const handleModifySave = async () => {
    if (!modifyForm.id) return alert("No character selected for modification.");
    try {
      const oldSafeName = modifyCharacter.name.replace(/\s+/g, "").replace(/[^\w]/g, "");
      const newSafeName = modifyForm.name.replace(/\s+/g, "").replace(/[^\w]/g, "");
      let image_url = modifyForm.image_url;
      let victory_audio = modifyForm.victory_audio;

      // IMAGE
      if (modifyForm.imageFile) {
        let uploadFile = modifyForm.imageFile;
        uploadFile = await resizeToPortrait(uploadFile, 270, 480);

        // Check if image with new name already exists
        const imagePath = `images/characters_pictures/${newSafeName}Picture.webp`;
        const { data: existsData } = await supabase.storage.from(BUCKET_NAME).list("images/characters_pictures", {
          search: `${newSafeName}Picture.webp`
        });

        let finalImagePath = imagePath;
        if (existsData && existsData.length > 0) {
          // If exists, use v2 and delete old one
          finalImagePath = `images/characters_pictures/${newSafeName}Picture_v2.webp`;
          await supabase.storage.from(BUCKET_NAME).remove([imagePath]);
        }

        // Remove old image if it exists (with old name)
        const oldImagePath = `images/characters_pictures/${oldSafeName}Picture.webp`;
        if (oldImagePath !== finalImagePath) {
          await supabase.storage.from(BUCKET_NAME).remove([oldImagePath]);
        }

        // Upload new image
        const { data: imgData, error: imgError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(finalImagePath, uploadFile, { 
            upsert: true,
            contentType: 'image/webp',
            cacheControl: '31536000' // 1 year in seconds
          });
        if (imgError) throw imgError;
        const { data: imgPublic } = supabase.storage.from(BUCKET_NAME).getPublicUrl(imgData.path);
        image_url = imgPublic?.publicUrl;
      }

      // AUDIO
      if (modifyForm.audioFile) {
        const oldAudioPath = `audio/characters_victory/${oldSafeName}Victory`;
        await supabase.storage.from(BUCKET_NAME).remove([oldAudioPath]);
        const { data: audioData, error: audioError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(`audio/characters_victory/${newSafeName}Victory`, modifyForm.audioFile, { upsert: true });
        if (audioError) throw audioError;
        const { data: audioPublic } = supabase.storage.from(BUCKET_NAME).getPublicUrl(audioData.path);
        victory_audio = audioPublic?.publicUrl;
      }

      // UPDATE CHARACTER
      const { error: updateError } = await supabase
        .from("characters")
        .update({
          name: modifyForm.name,
          manga: modifyForm.manga,
          affiliation: modifyForm.affiliation,
          role: modifyForm.role,
          gender: modifyForm.gender,
          rarity: modifyForm.rarity, // Include rarity in the update
          image_url,
          victory_audio,
        })
        .eq("id", modifyForm.id);

      if (updateError) throw updateError;

      // Refresh character list
      await fetchCharacters();

      alert("Character updated successfully!");
      setModifyCharacter(null);
      setModifyForm({
        id: null,
        name: "",
        manga: "",
        affiliation: [],
        role: "",
        gender: "",
        rarity: "",
        imageFile: null,
        audioFile: null,
        image_url: null,
        victory_audio: null,
      });
    } catch (err) {
      console.error("Error updating character:", err);
      alert("Error updating character. Check console for details.");
    }
  };

  const handlePlayAudio = (url) => {
    if (!url) return;
    // Add cache-busting query string
    const cacheBustedUrl = url + "?t=" + new Date().getTime();
    if (playingAudio && playingAudio.src === cacheBustedUrl) {
      playingAudio.pause();
      setPlayingAudio(null);
    } else {
      if (playingAudio) playingAudio.pause();
      const audio = new Audio(cacheBustedUrl);
      audio.play();
      setPlayingAudio(audio);
    }
  };

  // Delete character handler
  const handleDeleteCharacter = async () => {
    if (!modifyForm.id) return;
    const confirmation = window.prompt('Type DELETE to confirm character deletion:');
    if (confirmation !== 'DELETE') {
      alert('Deletion cancelled.');
      return;
    }
    try {
      // Remove image and audio from storage if they exist
      const safeName = modifyCharacter.name.replace(/\s+/g, "").replace(/[^\w]/g, "");
      if (modifyCharacter.image_url) {
        const imagePath = `images/characters_pictures/${safeName}Picture.webp`;
        await supabase.storage.from(BUCKET_NAME).remove([imagePath]);
      }
      if (modifyCharacter.victory_audio) {
        const audioPath = `audio/characters_victory/${safeName}Victory`;
        await supabase.storage.from(BUCKET_NAME).remove([audioPath]);
      }
      // Delete from database
      const { error } = await supabase
        .from("characters")
        .delete()
        .eq("id", modifyForm.id);

      if (error) throw error;
      await fetchCharacters();
      alert("Character deleted.");
      setModifyCharacter(null);
      setModifyForm({
        id: null,
        name: "",
        manga: "",
        affiliation: [],
        role: "",
        gender: "",
        imageFile: null,
        audioFile: null,
        image_url: null,
        victory_audio: null,
      });
    } catch (err) {
      console.error("Error deleting character:", err);
      alert("Error deleting character. Check console for details.");
    }
  };

  // Show loading while checking session/role
  if (session && userRole === null) {
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <Typography variant="h5">Checking permissions...</Typography>
      </Box>
    );
  }

  // Block access if not admin
  if (!session || userRole !== "admin") {
    return (
      <Box sx={{ p: 5, textAlign: "center" }}>
        <Typography variant="h4" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography>
          This page is only accessible to admin users.
        </Typography>
        {session && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LogoutIcon />}
            sx={{ mt: 3 }}
            onClick={handleLogout}
          >
            Log Out
          </Button>
        )}
      </Box>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto", minHeight: "100vh" }}>
        {/* Spacer for navbar */}
        <Box sx={{ height: { xs: 72, sm: 80 }, mb: { xs: 2, sm: 3 } }} />
        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            mb: 4,
            textAlign: "center",
            fontWeight: 900,
            letterSpacing: 2,
            fontSize: { xs: 36, sm: 48, md: 56 },
            color: "#fff",
          }}
        >
          Character Manager
        </Typography>

        {/* --- Character Creation Form --- */}
        {userRole === "admin" && (
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 5,
              maxWidth: 500,
              background: "linear-gradient(135deg, #232323 0%, #232323 60%, #2e2e2e 100%)",
              border: "2.5px solid #3e3e3e",
              color: "#fff",
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ color: "#fff" }}>
              Add New Character
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Name"
                value={form.name}
                onChange={e => handleChange("name", e.target.value)}
                required
                sx={{
                  background: "#232323",
                  borderRadius: 2,
                  input: { color: "#fff" },
                  label: { color: "#fff" },
                }}
                InputLabelProps={{ style: { color: "#fff" } }}
                InputProps={{ style: { color: "#fff" } }}
              />
              <FormControl required sx={{ background: "#232323", borderRadius: 2 }}>
                <InputLabel sx={{ color: "#fff" }}>Manga</InputLabel>
                <Select
                  value={form.manga}
                  label="Manga"
                  onChange={e => handleChange("manga", e.target.value)}
                  sx={{ color: "#fff" }}
                >
                  {mangaOptions.map((m) => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
                </Select>
              </FormControl>
              {/* Add Rarity Dropdown */}
              <FormControl required sx={{ background: "#232323", borderRadius: 2 }}>
                <InputLabel sx={{ color: "#fff" }}>Rarity</InputLabel>
                <Select
                  value={form.rarity}
                  label="Rarity"
                  onChange={e => handleChange("rarity", e.target.value)}
                  sx={{ color: "#fff" }}
                >
                  {rarityOptions.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl sx={{ background: "#232323", borderRadius: 2 }}>
                <InputLabel sx={{ color: "#fff" }}>Affiliation</InputLabel>
                <Select
                  value=""
                  label="Affiliation"
                  onChange={e => handleAffiliationSelect(e.target.value)}
                  sx={{ color: "#fff" }}
                >
                  <MenuItem value="">Select Affiliation</MenuItem>
                  {affiliationOptions.filter(opt => !form.affiliation.includes(opt)).map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                {form.affiliation.map(a => (
                  <Chip
                    key={a}
                    label={a}
                    onDelete={() => removeAffiliation(a)}
                    sx={{ mr: 1, mb: 1, background: "#232323", color: "#fbbf24" }}
                  />
                ))}
              </Box>
              <FormControl required sx={{ background: "#232323", borderRadius: 2 }}>
                <InputLabel sx={{ color: "#fff" }}>Role</InputLabel>
                <Select
                  value={form.role}
                  label="Role"
                  onChange={e => handleChange("role", e.target.value)}
                  sx={{ color: "#fff" }}
                >
                  {roleOptions.map(r => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl required sx={{ background: "#232323", borderRadius: 2 }}>
                <InputLabel sx={{ color: "#fff" }}>Gender</InputLabel>
                <Select
                  value={form.gender}
                  label="Gender"
                  onChange={e => handleChange("gender", e.target.value)}
                  sx={{ color: "#fff" }}
                >
                  {genderOptions.map(g => (<MenuItem key={g} value={g}>{g}</MenuItem>))}
                </Select>
              </FormControl>
              
              {/* Updated image upload button */}
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Button 
                  variant="contained" 
                  onClick={() => handleImageButtonClick(false)}
                  sx={{ 
                    flex: 1,
                    background: form.imageFile ? "#4caf50" : "#fbbf24", 
                    color: "#232323", 
                    fontWeight: 700 
                  }}
                >
                  {form.imageFile ? "Image Selected ✓" : "Upload Image"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handlePasteImage(false)}
                  sx={{
                    color: "#fbbf24",
                    borderColor: "#fbbf24",
                    fontWeight: 700,
                    minWidth: "120px",
                    "&:hover": {
                      borderColor: "#fff176",
                      color: "#fff176",
                    },
                  }}
                >
                  Paste Image
                </Button>
              </Stack>
              {pasteError && (
                <Typography sx={{ color: "#ff6b6b", fontSize: 14, mt: 1 }}>
                  {pasteError}
                </Typography>
              )}
              
              <Button variant="outlined" component="label" sx={{ color: "#fbbf24", borderColor: "#fbbf24", fontWeight: 700 }}>
                Upload Victory Audio
                <input type="file" accept="audio/*" hidden onChange={e => handleChange("audioFile", e.target.files[0])} />
              </Button>
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2, background: "#fbbf24", color: "#232323", fontWeight: 700 }}>
                Save Character
              </Button>
            </Box>
          </Paper>
        )}

        {/* Hidden file input for image upload */}
        <input
          type="file"
          accept="image/*"
          hidden
          ref={imageInputRef}
          onChange={(e) => handleImageUpload(e, e.target.dataset.isModify === 'true')}
        />

        {/* --- Search and Filter Controls --- */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4, flexWrap: "wrap" }}>
          <TextField
            label="Search by name"
            variant="outlined"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{
              minWidth: 180,
              background: "#232323",
              borderRadius: 2,
              input: { color: "#fff" },
              label: { color: "#fff" },
            }}
            InputLabelProps={{ style: { color: "#fff" } }}
            InputProps={{ style: { color: "#fff" } }}
          />
          <FormControl sx={{ minWidth: 120, background: "#232323", borderRadius: 2 }}>
            <InputLabel sx={{ color: "#fff" }}>Manga</InputLabel>
            <Select
              value={filter.manga}
              label="Manga"
              onChange={e => setFilter(f => ({ ...f, manga: e.target.value }))}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="">All Manga</MenuItem>
              {mangaOptions.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120, background: "#232323", borderRadius: 2 }}>
            <InputLabel sx={{ color: "#fff" }}>Gender</InputLabel>
            <Select
              value={filter.gender}
              label="Gender"
              onChange={e => setFilter(f => ({ ...f, gender: e.target.value }))}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="">All Genders</MenuItem>
              {genderOptions.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120, background: "#232323", borderRadius: 2 }}>
            <InputLabel sx={{ color: "#fff" }}>Role</InputLabel>
            <Select
              value={filter.role}
              label="Role"
              onChange={e => setFilter(f => ({ ...f, role: e.target.value }))}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="">All Roles</MenuItem>
              {roleOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120, background: "#232323", borderRadius: 2 }}>
            <InputLabel sx={{ color: "#fff" }}>Affiliation</InputLabel>
            <Select
              value={filter.affiliation}
              label="Affiliation"
              onChange={e => setFilter(f => ({ ...f, affiliation: e.target.value }))}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="">All Affiliations</MenuItem>
              {affiliationOptions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120, background: "#232323", borderRadius: 2 }}>
            <InputLabel sx={{ color: "#fff" }}>Rarity</InputLabel>
            <Select
              value={filter.rarity}
              label="Rarity"
              onChange={e => setFilter(f => ({ ...f, rarity: e.target.value }))}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="">All Rarities</MenuItem>
              {rarityOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => setFilter({ manga: "", gender: "", role: "", affiliation: "" })} sx={{ color: "#fff", borderColor: "#888" }}>
            Clear Filters
          </Button>
        </Stack>

        {/* Character List Section */}
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            color: "#fff", 
            fontWeight: 700,
            textAlign: "center",
            mb: 4,
            fontSize: { xs: 20, sm: 24 }
          }}
        >
          Character List ({totalCount} characters)
        </Typography>
        
        {/* Character Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
            gap: 4,
            justifyItems: "center",
            mb: 6,
          }}
        >
          {characters.map((char) => (
            <Paper
              key={char.id}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                p: 3,
                borderRadius: 4,
                minHeight: 360,
                width: 380,
                gap: 3,
                background: "linear-gradient(135deg, #232323 0%, #232323 60%, #2e2e2e 100%)",
                border: "2px solid #3e3e3e",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#f49330",
                  boxShadow: "0 8px 32px rgba(244, 147, 48, 0.2)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <LazyImage
                src={char.image_url}
                alt={char.name}
                threshold={0.2}
                sx={{
                  width: 120,
                  height: 200,
                  objectFit: "cover",
                  borderRadius: 3,
                  border: "2px solid #444",
                  background: "#181818",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", mb: 2 }}>
                  {char.name}
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0", mb: 1 }}>
                    Manga: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>{char.manga}</Box>
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0", mb: 1 }}>
                    Role: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>{char.role}</Box>
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0", mb: 1 }}>
                    Gender: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>{char.gender}</Box>
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0", mb: 1 }}>
                    Affiliation: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>
                      {Array.isArray(char.affiliation) ? char.affiliation.join(", ") : ""}
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0" }}>
                    Rarity: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>{char.rarity || "Unknown"}</Box>
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Stack spacing={2}>
                  {char.victory_audio && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handlePlayAudio(char.victory_audio)}
                      sx={{
                        color: "#f49330",
                        borderColor: "#f49330",
                        borderRadius: 3,
                        py: 1,
                        fontSize: 14,
                        fontWeight: 600,
                        "&:hover": {
                          background: "rgba(244, 147, 48, 0.1)",
                          borderColor: "#fff176",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Play Audio
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setModifyCharacter(char);
                      setModifyForm({ ...char, imageFile: null, audioFile: null });
                    }}
                    sx={{
                      background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                      color: "#181818",
                      fontWeight: 700,
                      borderRadius: 3,
                      py: 1,
                      fontSize: 14,
                      boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                        boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Modify Character
                  </Button>
                </Stack>
              </Box>
            </Paper>
          ))}
        </Box>

        {characters.length === 0 && (
          <Box sx={{ 
            textAlign: "center", 
            py: 8,
            background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
            borderRadius: 4,
            border: "2px solid #3e3e3e",
          }}>
            <Typography sx={{ color: "#e0e0e0", fontSize: 24, fontWeight: 600 }}>
              No characters found
            </Typography>
            <Typography sx={{ color: "#bbb", fontSize: 16, mt: 2 }}>
              Try adjusting your search criteria
            </Typography>
          </Box>
        )}

        {/* Pagination Controls */}
        {Math.ceil(totalCount / pageSize) > 1 && (
          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center", 
            alignItems: "center",
            gap: 4,
            mt: 6 
          }}>
            <Pagination
              count={Math.ceil(totalCount / pageSize)}
              page={page}
              onChange={(_, value) => setPage(value)}
              size="large"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#fff",
                  borderColor: "#555",
                  fontSize: 18,
                  height: 48,
                  minWidth: 48,
                  "&:hover": {
                    backgroundColor: "rgba(244, 147, 48, 0.1)",
                    borderColor: "#f49330",
                  },
                },
                "& .Mui-selected": {
                  background: "linear-gradient(45deg, #f49330 30%, #fff176 90%) !important",
                  color: "#181818 !important",
                  borderColor: "transparent !important",
                  fontWeight: 700,
                },
              }}
            />
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel sx={{ color: "#bbb", "&.Mui-focused": { color: "#f49330" } }}>Items per page</InputLabel>
              <Select
                value={pageSize}
                label="Items per page"
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                sx={{ 
                  color: "#fff", 
                  background: "#1a1a1a", 
                  borderRadius: 2,
                  height: 48,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                }}
              >
                {[5, 10, 20, 50].map(size => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Modify Character Modal */}
        <Modal
          open={!!modifyCharacter}
          onClose={() => setModifyCharacter(null)}
          aria-labelledby="modify-character-modal"
        >
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              p: 3,
              borderRadius: 2,
              outline: "none",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Modify Character</Typography>
              <IconButton onClick={() => setModifyCharacter(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                label="Name"
                value={modifyForm.name}
                onChange={e => handleChange("name", e.target.value, true)}
              />
              <FormControl>
                <InputLabel>Manga</InputLabel>
                <Select
                  value={modifyForm.manga}
                  label="Manga"
                  onChange={e => handleChange("manga", e.target.value, true)}
                >
                  {mangaOptions.map(m => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Rarity</InputLabel>
                <Select
                  value={modifyForm.rarity}
                  label="Rarity"
                  onChange={e => handleChange("rarity", e.target.value, true)}
                >
                  {rarityOptions.map(r => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Affiliation</InputLabel>
                <Select
                  value=""
                  label="Affiliation"
                  onChange={e => handleAffiliationSelect(e.target.value, true)}
                >
                  <MenuItem value="">Select Affiliation</MenuItem>
                  {affiliationOptions.filter(opt => !modifyForm.affiliation.includes(opt)).map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                {modifyForm.affiliation.map(a => (
                  <Chip
                    key={a}
                    label={a}
                    onDelete={() => removeAffiliation(a, true)}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
              <FormControl>
                <InputLabel>Role</InputLabel>
                <Select
                  value={modifyForm.role}
                  label="Role"
                  onChange={e => handleChange("role", e.target.value, true)}
                >
                  {roleOptions.map(r => (<MenuItem key={r} value={r}>{r}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={modifyForm.gender}
                  label="Gender"
                  onChange={e => handleChange("gender", e.target.value, true)}
                >
                  {genderOptions.map(g => (<MenuItem key={g} value={g}>{g}</MenuItem>))}
                </Select>
              </FormControl>
              
              {/* Updated image upload button for modify */}
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Button 
                  variant="contained" 
                  onClick={() => handleImageButtonClick(true)}
                  sx={{ 
                    flex: 1,
                    background: modifyForm.imageFile ? "#4caf50" : "#fbbf24", 
                    color: "#232323", 
                    fontWeight: 700 
                  }}
                >
                  {modifyForm.imageFile ? "New Image Selected ✓" : "Upload New Image"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handlePasteImage(true)}
                  sx={{
                    color: "#fbbf24",
                    borderColor: "#fbbf24",
                    fontWeight: 700,
                    minWidth: "120px",
                    "&:hover": {
                      borderColor: "#fff176",
                      color: "#fff176",
                    },
                  }}
                >
                  Paste Image
                </Button>
              </Stack>
              
              <Button variant="outlined" component="label">
                Upload Victory Audio
                <input type="file" accept="audio/*" hidden onChange={e => handleChange("audioFile", e.target.files[0], true)} />
              </Button>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handleModifySave}>
                  Save Changes
                </Button>
                <Button variant="outlined" onClick={() => setModifyCharacter(null)}>
                  Close
                </Button>
                {userRole === "admin" && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteCharacter}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            </Box>
          </Paper>
        </Modal>

        {/* Image Crop Dialog */}
        <Dialog 
          open={imageCropDialogOpen} 
          onClose={() => setImageCropDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
              border: "2px solid #f49330",
              borderRadius: 4,
            }
          }}
        >
          <DialogTitle sx={{ color: "#fff", fontWeight: 700, fontSize: 24 }}>
            Crop Character Image (9:16 ratio)
          </DialogTitle>
          <DialogContent>
            {imagePreview && (
              <Box sx={{ 
                position: "relative", 
                width: "100%", 
                height: 500, 
                background: "#181818", 
                borderRadius: 3, 
                overflow: "hidden",
                mb: 2
              }}>
                <Cropper
                  image={imagePreview}
                  crop={crop}
                  zoom={zoom}
                  aspect={9/16} // 9:16 aspect ratio for character images
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: {
                      borderRadius: 12,
                    }
                  }}
                />
              </Box>
            )}
            <Typography sx={{ color: "#bbb", fontSize: 14, textAlign: "center" }}>
              Adjust the crop area to select the 9:16 portion of your image that will be used for the character
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setImageCropDialogOpen(false)}
              sx={{ 
                color: "#999",
                "&:hover": { color: "#fff" }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleImageSave}
              sx={{
                background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                color: "#181818",
                fontWeight: 700,
                px: 4,
                "&:hover": {
                  background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                },
              }}
            >
              Save Cropped Image
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageContainer>
  );
}
